import {css, html, LitElement, nothing} from "lit";
import {directive, Directive} from "lit/directive.js";
import {mix} from "mixwith";
import {StateMixin} from "./Mixins/StateMixin.js";
import MutableMixin from "./Mixins/MutableMixin.js";
import {ifDefined} from "lit/directives/if-defined.js";
import {CancelablePromise, CancelledError} from "../CancelablePromise.js";
import {deepCopy} from "../Utils/ObjectUtils.js";
import "./aui-autosize-input.js";
import "./Select/aui-option.js";

// Validate
// Form
// Some sort of name,value behavior
// The input is in the state floating, when the dropdown don't fit into the screen.
// filtering
// XInputs.FixedPositionMixin(XList.XSelectionMixin(XList.XFocusMixin(XList.DOMListMixin(XInputs.XDropdownMixin
// Polymer.IronControlState,
// Polymer.IronA11yKeysBehavior,
// Polymer.IronFormElementBehavior,
// Polymer.IronValidatableBehavior

// single => in{} => out{}
// multi => in{{}} => out[{}]

// async single => in{label,value} => out{value}
// async multi => in[{label,value}] => out[{value}]

// Keyboard

// TODO: Local Filtering
// TODO: Async Request caching
// TODO: Async Request delay when entering input

// TODO: Creatable
// TODO: Fixed Options
// TODO: Scroll to focus
// TODO: Focus value of isMulti

// TODO: State controls
// TODO: Custom styles
// TODO: Format option label
// TODO: Format group label
// TODO: Custom components
// TODO: RTL
// Menu Groups
// TODO: Animations
// Empty Options / No Filter matches
// TODO: Option tooltips

/**
 * @typedef {{ label: string, value }} SelectOption
 */

/**
 * Converts an object into an option format.
 *
 * @param {{}} item
 * @param {string} labelKey
 * @param {string} valueKey
 * @return {{ label: string, value }|null}
 */
export function toOption(item, labelKey = "Name", valueKey = "ID") {
	if(!item) return null;
	return { label: item[labelKey] || item[valueKey], value: item[valueKey] };
}

/**
 * Converts an option to an output object.
 *
 * @param {SelectOption|[SelectOption]} option
 * @param {string} labelKey
 * @param {string} valueKey
 * @return {{}}
 */
export function fromOption(option, labelKey = "Name", valueKey = "ID") {
	if(!option) return option;

	if(Array.isArray(option))
		return option.map(o => fromOption(o, labelKey, valueKey));

	const output = {};

	if(option.label) output[labelKey] = option.label;
	output[valueKey] = option.value;

	return output;
}

class FormatOption extends Directive {

	render(item, labelKey = "Name", valueKey = "ID") {
		if(item === null || typeof item === "object")
			return html`${toOption(item, labelKey, valueKey)}`;
		return nothing;
	}

}

/**
 * Converts an object into an option format.
 */
export const formatOption = directive(FormatOption);

class FormatOptions extends Directive {

	render(array, labelKey = "Name", valueKey = "ID") {
		if(!Array.isArray(array))
			return nothing;

		return array.map(item => ({ label: item[labelKey], value: item[valueKey] }));
	}

}

/**
 * Converts an array of objects into an options format.
 */
export const formatOptions = directive(FormatOptions);

/**
 * An dropdown. An input which opens an menu to choose options with the mouse or keyboard.
 * By typing an value in the input, options from the menu can be filtered or new options be created.
 *
 * The options can be set statically or loaded dynamically.
 * The options and the value can be anything but in order that the menu can display the options and value correctly, it is
 * mandatory that the option and value is an object which have an label and value property.
 * E.g.: { label: "Donut", value: "donut" }
 * The label is optional and value will be used instead if not present.
 *
 * The value and the options are strictly separated, meaning that the value can be set to anything regardless if option
 * exists or not. This allows that the value can be set immediately and the options to be loaded dynamically without
 * anybody have to wait.
 * The menu will just highlight the value if it can be found in the menu options.
 *
 * Usage Examples:
 *
 * Static Options:
 * const data = [
 *      { label: "Ocean", value: "Ocean" },
 *      { label: "Blue", value: "Blue" },
 *      { label: "Purple", value: "Purple" },
 *      { label: "Red", value: "Red" },
 *      { label: "Sliver", value: "Sliver" }
 * ];
 *
 * Static Select:
 * <aui-select placeholder="Choose one..." .options=${data}></aui-select>
 *
 * Static Select with preselected option:
 * <aui-select placeholder="Choose one..." .options=${data} .value=${data[1]}></aui-select>
 *
 * Static Multi-Select:
 * <aui-select multi placeholder="Choose one or more" .options=${data}></aui-select>
 *
 * Static Multi-Select with preselected options:
 * <aui-select multi placeholder="Choose one or more" .options=${data} .value=${[data[1], data[2]]}></aui-select>
 *
 * Dynamic data loaded using promises:
 * const filter = (label, inputValue) => {
 *      if(label.toLowerCase().includes(inputValue.trim().toLowerCase()))
 *          return true;
 *      return false;
 * };
 *
 * const promiseOptions = (inputValue, onCancel) => {
 *      return new Promise((resolve, reject) => {
 *          const timer = setTimeout(() => {
 *              let d = data;
 *              if(!!inputValue)
 *                  d = d.filter((option) => filter(option.label, inputValue || ""));
 *              resolve(d);
 *          }, 1000);
 *
 *          onCancel(() => { clearTimeout(timer); });
 *      });
 * };
 *
 * Dynamic Select using Promises (options will be loaded once the menu opens):
 * <aui-select placeholder="Choose one..." .loadOptions=${promiseOptions}></aui-select>
 *
 * Dynamic Select using Promises (options will be loaded directly):
 * <aui-select placeholder="Choose one..." .loadOptions=${promiseOptions} .defaultOptions=${true}></aui-select>
 *
 * The options can also have groups, data should look like this:
 * const dataWithGroups = [
 *      { label: "First", value: "First" },
 *      { label: "Colors", options: data },
 *      {
 *          label: "Flavours", options: [
 *              { label: "Vanilla", value: "Vanilla" },
 *              { label: "Chocolate", value: "Chocolate" },
 *              { label: "Strawberry", value: "Strawberry" }
 *          ]
 *      },
 *      { label: "Last", value: "Last" }
 * ];
 *
 * For more examples and documentation visit: TODO: Create a documentation page with examples
 */
export default class AuiSelect extends mix(LitElement).with(StateMixin, MutableMixin) {

	static get properties() {
		return {
			// Options
			focusedOption: { attribute: false },

			// State
			isMulti: { type: Boolean, attribute: "multi", reflect: true },
			isMenuOpen: { type: Boolean, attribute: "menu-open", reflect: true },
			isClearable: { type: Boolean, attribute: "clearable", reflect: true },
			isSearchable: { type: Boolean, attribute: "searchable", reflect: true },
			isCreatable: { type: Boolean, attribute: "creatable", reflect: true },
			isLoading: { type: Boolean, attribute: "loading", reflect: true },
			isRTL: { type: Boolean, attribute: "rtl", reflect: true },

			// Select Value
			//value: { attribute: false, hasChanged: (value, oldValue) => { console.log(value, oldValue); } },
			defaultValue: String,
			placeholder: String,
			selectedValue: { attribute: false },
			focusedValue: { attribute: false },
			overflowTitle: { attribute: false },

			// Input Value
			/**
			 * Holds the currently typed input value.
			 */
			inputValue: String,
			defaultInputValue: String
		};
	}

	set options(newOptions) {
		const oldOptions = this.__options;

		this.__options = newOptions;
		this.menuOptions = this.buildMenuOptions(this.__options);

		this.requestUpdate("options", oldOptions);
	}

	get options() {
		return this.__options;
	}

	set value(newValue) {
		const oldValue = this.isMulti ? this.__oldValue : this.selectedValue;

		this.selectedValue = newValue ? (this.isMulti ? newValue : [newValue]) : [];

		this.requestUpdate("value", oldValue);
	}

	get value() {
		if(!this.hasValue())
			return this.isMulti ? [] : null;

		if(this.isMulti) {
			return this.selectedValue.map(option => ({
				label: this.getOptionLabel(option),
				value: this.getOptionValue(option)
			}));
		} else {
			return {
				label: this.getOptionLabel(this.selectedValue[0]),
				value: this.getOptionValue(this.selectedValue[0])
			};
		}
	}

	constructor() {
		super();
		this.setAttribute("tabindex", "0");
		this.addEventListener("mousedown", this._handleMouseDown.bind(this));
		this.addEventListener("keydown", this._handleKeyDown.bind(this));

		this.mutationOptions = {
			/**
			 * Tells the mutation API to just use the .value property of the SelectOption to detect changes.
			 */
			getCompareValue: option => option !== null ? option.value : option
		};

		//
		// Options
		//
		/**
		 * An function to load the options from a remote source.
		 *
		 * @type {function|null}
		 */
		this.loadOptions = null;

		/**
		 * The options set from outside or from the loadOptions function.
		 *
		 * @type {*[]}
		 */
		this.options = [];

		/**
		 * Holds the internal representation of .options
		 * render holds the options which can be rendered.
		 * focusable holds the options which are focusable.
		 *
		 * @type {{focusable: [], render: []}}
		 */
		this.menuOptions = { render: [], focusable: [] };

		/**
		 * Holds the default options to revert to when the element resets.
		 * When .loadOptions is set, this have to be set to true in order to load the default options.
		 *
		 * @type {*[]|boolean|null}
		 */
		this.defaultOptions = null;

		/**
		 * Holds the currently focused option in the menu.
		 *
		 * @type {null}
		 */
		this.focusedOption = null;

		//
		// State
		//
		this.isMulti = false;
		this.isMenuOpen = false;
		this.isClearable = true;
		this.isSearchable = false;
		this.isCreatable = false;
		this.isLoading = false;
		this.isRTL = false;

		//
		// Selection & Value
		//

		/**
		 * Holds the default value to revert to when the element resets or no value is set.
		 *
		 * @type {null}
		 */
		this.defaultValue = null;

		/**
		 * Holds the current value.
		 * Is always an array, when isMulti is true it holds all selected options, otherwise it holds
		 * the selected option at index 0.
		 *
		 * Notice: The .value is computed from this value.
		 *
		 * @type {*[]}
		 */
		this.selectedValue = [];

		/**
		 * The displayed input value when no value or typed value is set.
		 *
		 * @type {string|null}
		 */
		this.placeholder = null;

		/**
		 * TODO: Implement focus for selected value/options for isMulti. Use left/right buttons to focus.
		 * @type {null}
		 */
		this.focusedValue = null;

		/**
		 * If the placeholder or selected value does overflow and is truncated, add an title attribute.
		 *
		 * @type {string|null}
		 */
		this.overflowTitle = undefined;

		//
		// Input Value
		//

		/**
		 * Holds the currently typed input value.
		 *
		 * @type {string|null}
		 */
		this.inputValue = null;

		/**
		 * TODO: Implement default inputValue.
		 *
		 * @type {string|null}
		 */
		this.defaultInputValue = null;

		// Public Control
		/**
		 * Defines if the menu should close when an option gets selected.
		 *
		 * @type {boolean}
		 */
		this.closeMenuOnSelect = !this.isMulti;

		/**
		 * Defines if the selected options should be hidden in the menu.
		 *
		 * @type {boolean}
		 */
		this.hideSelectedOptions = true;

		/**
		 * Will remove any selected value if not in options.
		 *
		 * @type {boolean}
		 */
		this.deselectNonExistingValue = true;

		/**
		 * Defines if an selected value should be popped when backspace was pressed.
		 * When multiple options got selected, the backspace will then pop the latest selected.
		 * Only used for isMulti = true
		 *
		 * @type {boolean}
		 */
		this.backspaceRemovesValue = true;

		//
		// Internal Control
		//
		this._openMenuAfterFocus = false;
	}

	/**
	 * @override
	 */
	resetChanges() {
		this.value = deepCopy(this.defaultValue);
	}

	/**
	 * @override
	 */
	saveChanges(newValue = undefined) {
		if(newValue !== undefined)
			this.value = newValue;
		this.defaultValue = deepCopy(this.value);
		this.setInitialValue(this.defaultValue);
		this.updateChanges(this.defaultValue);
	}

	firstUpdatedBeforeRender() {
		super.firstUpdatedBeforeRender();

		// If closeMenuOnSelect is not manually set, set by default to true for isMulti === false
		if(!this.hasAttribute("close-menu-on-select"))
			this.closeMenuOnSelect = !this.isMulti;

		if(!this.hasInitialValue()) {
			// Sets the initial value as defaultValue which will be used when the element resets.
			if(this.defaultValue === null)
				this.defaultValue = deepCopy(this.value);

			// Sets the initial value.
			this.setInitialValue(this.defaultValue);
		}

		if((this.isMulti ? !this.value.length : !this.value) && this.defaultValue)
			this.value = this.defaultValue;
	}

	update(changedProperties) {
		if(changedProperties.size === 0 || changedProperties.has("selectedValue")) {
			if(this.isMulti) {
				/**
				 * Holds a copy of previous selected options, right after it was set.
				 * Cannot be fetched right before in the items setter, because the value
				 * is an array reference it can be changed without notice using array methods like
				 * push/splice/etc. and we could not get the "old" options.
				 *
				 * @type {[*]}
				 * @private
				 */
				this.__oldValue = deepCopy(this.value);
			}

			// Update Mutation.
			{
				this.updateChanges(this.value);

				// Update binding
				const event = new CustomEvent("change", {
					composed: true,
					detail: {
						value: deepCopy(this.value)
					}
				});

				this.dispatchEvent(event);
			}

			this.updateSelectedValue();
		}

		super.update(changedProperties);
	}

	firstUpdated() {
		//console.log("ready", this.value, this);

		if(!this.value && this.defaultValue)
			this.value = this.defaultValue;

		this.menuOptions = this.buildMenuOptions(this.options);

		if(this.defaultOptions === true) {
			this.isLoading = true;
			this.fetchOptions(this.inputValue || this.defaultInputValue).then(options => {
				this.defaultOptions = options;
				this.options = options;
			}).finally(() => this.isLoading = !!this._lastPromise);
		}
	}

	updated(changedProperties) {
		this.updateOverflowTitle(changedProperties);
	}

	updateSelectedValue() {
		if(!this.deselectNonExistingValue)
			return;

		if(!this.hasValue())
			return;

		if(this.isMulti) {
			// TODO
		} else {
			let found = false;

			const value = this.getOptionValue(this.selectedValue[0]);
			let focusedIndex = this.menuOptions.focusable.findIndex(o => this.getOptionValue(o) === value);
			if(focusedIndex > -1 && this.menuOptions.focusable[focusedIndex])
				found = true;

			if(!found)
				this.clearValue();
		}
	}

	updateOverflowTitle(changedProperties) {
		const updateTitleOn = [
			"isMulti",
			"selectedValue",
			"defaultValue",
			"placeholder",
			"inputValue",
			"defaultInputValue"
		];

		if(updateTitleOn.some(property => changedProperties.has(property))) {
			this.overflowTitle = this.shouldAddTitle() ? (this.hasValue() ? this.getOptionLabel(this.selectedValue[0]) : this.placeholder) : undefined;
		}
	}

	shouldAddTitle() {
		let addTitle = !this.inputValue && !this.isMulti && !!(this.placeholder || this.getOptionLabel(this.selectedValue[0]));

		if(addTitle) {
			const valueContainer = this.shadowRoot.querySelector("#ValueContainer");
			const placeholderOrValue = this.shadowRoot.querySelector(".placeholderOrValue");

			addTitle = placeholderOrValue.scrollWidth > valueContainer.clientWidth;
		}

		return addTitle;
	}

	//
	// Options
	//

	hasOptions() {
		return this.menuOptions.render.length > 0;
	}

	getOptionLabel(option) {
		return option.label || option.value;
	}

	getOptionValue(option) {
		return option.value;
	}

	isOptionDisabled(option) {
		return !!option.isDisabled;
	}

	isOptionSelected(option) {
		if(this.selectedValue.indexOf(option) > -1)
			return true;

		const value = this.getOptionValue(option);
		return this.selectedValue.some(v => this.getOptionValue(v) === value);
	}

	shouldHideSelectedOptions() {
		return this.hideSelectedOptions && this.isMulti;
	}

	/**
	 * Return true if the option should be filtered/hidden from the menu.
	 *
	 * @param {string} inputValue
	 * @param {string} value
	 * @param {string} label
	 * @param {object} option
	 * @return {boolean}
	 */
	filterOption(inputValue, value, label, option) {
		if(label.toLowerCase().includes(inputValue.trim().toLowerCase()))
			return false;
		return true;
	}

	focusOption(direction = "first") {
		const options = this.menuOptions.focusable;
		if(!options.length)
			return;

		let nextFocus = 0; // Handles "first"
		let focusedIndex = options.indexOf(this.focusedOption);

		switch(direction) {
			case "up":
				nextFocus = focusedIndex > 0 ? focusedIndex - 1 : options.length - 1;
				break;
			case "down":
				nextFocus = (focusedIndex + 1) % options.length;
				break;
			case "pageup":
				break;
			case "pagedown":
			case "last":
				nextFocus = options.length - 1;
				break;
		}

		this.focusedOption = options[nextFocus];
		// TODO: ARIA
	}

	focusSelectedOption() {
		if(this.isMulti || !this.hasValue())
			return;

		const value = this.getOptionValue(this.selectedValue[0]);
		let focusedIndex = this.menuOptions.focusable.findIndex(o => this.getOptionValue(o) === value);
		if(focusedIndex > -1)
			this.focusedOption = this.menuOptions.focusable[focusedIndex];
	}

	selectOption(option) {
		if(this.isMulti) {
			if(this.isOptionSelected(option)) {
				const value = this.getOptionValue(option);
				this.setValue(this.selectedValue.filter(v => this.getOptionValue(v) !== value));

				// TODO: ARIA: Announce that option got selected
			} else {
				if(!this.isOptionDisabled(option)) {
					this.setValue([...this.selectedValue, option]);

					// TODO: ARIA: Announce that option got selected
				} else {
					// TODO: ARIA: Announce that option is disabled
				}
			}
		} else {
			if(!this.isOptionDisabled(option)) {
				this.setValue([option]);

				// TODO: ARIA: Announce that option got selected
			} else {
				// TODO: ARIA: Announce that option is disabled
			}
		}
	}

	//
	// Value
	//

	hasValue() {
		return this.selectedValue.length > 0;
	}

	setValue(newValue) {
		this.selectedValue = newValue;

		if(this.isMenuOpen && this.closeMenuOnSelect)
			this.closeMenu();

		this.menuOptions = this.buildMenuOptions(this.options);
	}

	removeValue(removedValue) {
		const value = this.getOptionValue(removedValue);
		this.setValue(this.selectedValue.filter(v => this.getOptionValue(v) !== value));
	}

	popValue() {
		this.setValue(this.selectedValue.slice(0, this.selectedValue.length - 1));
	}

	clearValue() {
		this.setValue([]);
	}

	//
	// Event handlers
	//

	_handleInputChange(value) {
		this.inputValue = value;

		if(this.loadOptions !== null) {
			if(this.inputValue) {
				if(false) {
					// TODO: Caching
				} else {
					if(this._lastPromise)
						this._lastPromise.cancel();

					this.isLoading = true;
					this.options = [];

					this._lastPromise = this.fetchOptions(this.inputValue);
					this._lastPromise.then(options => {
						delete this._lastPromise;
						this.isLoading = false;

						this.options = options;
						this.focusOption("first");
					}).catch(reason => {
						if(reason instanceof CancelledError)
							return;

						throw reason;
					});
				}
			} else {
				if(this._lastPromise) {
					this._lastPromise.cancel();
					delete this._lastPromise;
					this.isLoading = false;
				}

				this.options = this.defaultOptions || [];
			}
		} else {
			this.menuOptions = this.buildMenuOptions(this.options);
		}

		if(!this.isMenuOpen)
			this.openMenu();
		this.focusOption("first");
	}

	_handleFocus(e) {
		// When this element receives focus, retarget focus to the internal input
		if(this.isSearchable || this.isCreatable)
			this.input.focus();

		if(this._openMenuAfterFocus) {
			this._openMenuAfterFocus = false;
			this.openMenu("first");
		}
	}

	_handleBlur(e) {
		this.closeMenu();
	}

	/**
	 * @override State
	 * @param e
	 */
	_focusBlurHandler(e) {
		super._focusBlurHandler(e);
		if(this.isFocused) this._handleFocus(e); else this._handleBlur(e);
	}

	//
	// Mouse handlers
	//

	_handleMouseDown(e) {
		if(!this.isFocused) {
			this._openMenuAfterFocus = true;
			this.focus();
		} else {
			const target = e.composedPath()[0];
			if(target.id === "InputContainer" || target.id === "ValueContainer" || target === this.input.input) {
				if((this.isSearchable || this.isCreatable) && this.inputValue) {
					if(!this.isMenuOpen) {
						this.openMenu("first");
					} else {
						if(target !== this.input.input)
							requestAnimationFrame(() => this.input.focus());
						return;
					}
				} else {
					this.toggleMenu("first");
				}
			}
		}

		e.preventDefault();
	}

	_handleClearIndicatorMouseDown(e) {
		// Ignore mouse events that weren't triggered by the primary button
		if(e && e.type === "mousedown" && e.button !== 0)
			return;

		e.stopPropagation();
		this.clearValue();
		requestAnimationFrame(() => this.focus());
	}

	_handleDropdownIndicatorMouseDown(e) {
		// Ignore mouse events that weren't triggered by the primary button
		if(e && e.type === "mousedown" && e.button !== 0)
			return;

		e.stopPropagation();
		e.preventDefault();

		if(!this.isFocused) {
			this._openMenuAfterFocus = true;
			this.focus();
		} else {
			this.toggleMenu("first");
		}
	}

	_handleMenuMouseDown(e) {
		if(e.button !== 0)
			return;

		e.stopPropagation();
		e.preventDefault();
	}

	_handleOptionHover(option) {
		this.focusedOption = option;
	}

	//
	// Keyboard handlers
	//

	_handleKeyDown(e) {
		if(this.isDisabled)
			return;

		switch(e.key) {
			// Navigation
			case "ArrowUp":
				if(this.isMenuOpen) {
					this.focusOption("up");
				} else {
					this.openMenu("last");
				}
				break;

			case "ArrowDown":
				if(this.isMenuOpen) {
					this.focusOption("down");
				} else {
					this.openMenu("first");
				}
				break;

			case "PageUp":
				if(!this.isMenuOpen) return;
				this.focusOption("pageup");
				break;

			case "PageDown":
				if(!this.isMenuOpen) return;
				this.focusOption("pagedown");
				break;

			case "Home":
				if(!this.isMenuOpen) return;
				this.focusOption("first");
				break;

			case "End":
				if(!this.isMenuOpen) return;
				this.focusOption("last");
				break;

			// Actions
			case "Enter":
				if(e.keyCode === 229) {
					// Ignore the keydown event from an Input Method Editor(IME)
					// ref. https://www.w3.org/TR/uievents/#determine-keydown-keyup-keyCode
					break;
				}

				if(!this.isMenuOpen) return;
				if(!this.focusedOption) return;
				this.selectOption(this.focusedOption);
				break;

			case "Escape":
				if(this.isMenuOpen)
					this.closeMenu();
				break;

			case "Backspace":
			case "Delete":
				if(this.inputValue) return;
				if(!this.hasValue()) return;
				if(!this.backspaceRemovesValue) return;
				if(this.isMulti)
					this.popValue();
				break;

			default:
				return;
		}

		// Stop default behavior if key got processed
		e.preventDefault();
	}

	//
	// Menu
	//

	openMenu(focusOption) {
		if(!this.isMulti && this.hasValue()) {
			this.focusSelectedOption();
		} else if(focusOption) {
			this.focusOption(focusOption);
		}

		this.isMenuOpen = true;
	}

	closeMenu() {
		this.isMenuOpen = false;
		this.focusedOption = null;

		if(this.loadOptions) {
			if(this._lastPromise) {
				this._lastPromise.cancel();
				delete this._lastPromise;
				this.isLoading = false;
			}

			if(this.inputValue)
				this.inputValue = null;
			this.options = this.defaultOptions || [];
		} else if(this.inputValue) {
			this.inputValue = null;
			this.menuOptions = this.buildMenuOptions(this.options);
		}
	}

	toggleMenu(focusOption) {
		if(this.isMenuOpen) this.closeMenu(); else this.openMenu(focusOption);
	}

	fetchOptions(inputValue) {
		if(!this.loadOptions)
			return CancelablePromise.resolve([]);

		return new CancelablePromise((resolve, reject, promise) => {
			const loader = this.loadOptions(inputValue, promise);

			// Promise or promise like
			if(loader && typeof loader.then === "function") {
				loader.then(resolve, reject);
			} else {
				resolve(loader);
			}
		});
	}

	/**
	 * Rebuilds the menu options, and creates states and filters options.
	 *
	 * Should be called when
	 * - options changes
	 * - selection/value changes
	 * - inputValue changes
	 *
	 * @param options
	 * @return {*}
	 */
	buildMenuOptions(options) {
		const addOption = (option) => {
			const isDisabled = this.isOptionDisabled(option);
			const isSelected = this.isOptionSelected(option);

			if(this.shouldHideSelectedOptions() && isSelected)
				return;

			const label = this.getOptionLabel(option);
			const value = this.getOptionValue(option);

			if(this.filterOption(this.inputValue || "", value, label, option))
				return;

			return {
				option: option,
				type: "option",
				isDisabled: isDisabled,
				isSelected: isSelected,
				label: label,
				value: value
			};
		};

		return options.reduce((menuOptions, option) => {
			if(option.options) {
				// Group
				const o = {
					type: "group",
					label: option.label,
					options: option.options.map(groupOption => {
						const o = addOption(groupOption);
						if(o) menuOptions.focusable.push(o);
						return o;
					}).filter(o => o !== undefined)
				};

				if(o.options.length)
					menuOptions.render.push(o);
			} else {
				const o = addOption(option);
				if(o) {
					menuOptions.render.push(o);
					menuOptions.focusable.push(o);
				}
			}

			return menuOptions;
		}, { render: [], focusable: [] });
	}

	//
	// Rendering
	//

	static get styles() {
		return css`
			:host {
				position: relative;
				display: inline-block;
				outline: none;
			}
			
			:host > .inputContainer {
				background: #FFF;
				display: flex;
				align-items: center;
				padding: 5px;
				border: 1px solid var(--divider-color);
				border-radius: 3px;
			}
			
			:host > .inputContainer aui-autosize-input {
				padding: 0;
				border: none;
				outline: none;
				font-family: inherit;
				font-size: inherit;
				color: inherit;
			}
			
			:host > .inputContainer .indicatorsContainer {
				display: flex;
				align-items: center;
				color: var(--secondary-text-color);
			}
			
			:host > .inputContainer .indicatorsContainer > .clear {
				padding: 0 5px;
				cursor: pointer;
			}
			
			:host > .inputContainer .indicatorsContainer > .dropdown {
				padding-left: 5px;
				border-left: 1px solid var(--divider-color);
				cursor: pointer;
			}
			
			:host .valueContainer {
				position: relative;
				flex: 1;
				display: flex;
				flex-wrap: wrap; /* For multi to wrap the values */
				overflow: hidden;
			}
			
			.placeholderOrValue {
				position: absolute;
				max-width: 100%;
				color: var(--secondary-text-color);
				overflow: hidden;
				white-space: nowrap;
				text-overflow: ellipsis;
				pointer-events: none;
			}
			
			:host > .menu {
				box-sizing: border-box;
				background: #FFF;
				position: absolute;
				width: 100%;
				border: 1px solid var(--primary-color);
				border-top: none;
				color: var(--primary-text-color);
				
				z-index: 1000;
			}
			
			.group {
			}
			
			.group > .label {
				padding: 5px;
				font-weight: bold;
			}
			
			.group > .options {
			}
			
			/* Hover state */
			:host(:hover) > .inputContainer {
				border-color: var(--primary-color); /* --theme-primary-variant */
			}
			
			:host(:hover) > .inputContainer .indicatorsContainer {
				color: var(--primary-text-color);
			}
			
			:host(:hover) > .inputContainer .indicatorsContainer > .dropdown {
				color: var(--primary-color);
			}
			
			:host > .inputContainer .indicatorsContainer > .clear:hover {
				color: var(--primary-color);
			}
			
			/* Disabled State */
			:host([disabled]) {
				opacity: 0.5;
			}
			
			/* Focus State */
			:host([focused]) > .inputContainer {
				border-color: var(--primary-color);
			}
			
			/*:host([focused]) > .inputContainer:after {
				content: '';
				display: block;
				position: absolute;
				top: -3px;
				bottom: -3px;
				left: -3px;
				right: -3px;
				border: 1px dashed var(--primary-color);
				border-radius: 3px;
				pointer-events: none;
			}*/
			
			/* Menu Open State */
			:host([menu-open]) > .inputContainer {
				border-bottom-color: var(--divider-color);
				border-radius: 3px 3px 0 0;
			}
		`;
	}

	get input() {
		if(!this._inputNode)
			this._inputNode = this.shadowRoot.querySelector("aui-autosize-input");
		return this._inputNode;
	}

	render() {
		return html`
            <section id="InputContainer" class="inputContainer">
                <div id="ValueContainer" class="valueContainer" title=${ifDefined(this.overflowTitle)}>
                    ${this.renderPlaceholderOrValue()}
                    ${this.renderInput()}
                </div>

                <div class="indicatorsContainer">
                    ${this.renderLoadingIndicator()}
                    ${this.renderClearIndicator()}
                    ${this.renderDropdownIndicator()}
                </div>
            </section>
            ${this.renderMenu()}
		`;
	}

	renderPlaceholderOrValue() {
		if(!this.hasValue()) {
			return this.inputValue ? nothing : html`
                <div class="placeholderOrValue">${this.placeholder}</div>`;
		}

		if(this.isMulti) {
			return html`${this.selectedValue.map(value => html`
                <div style="background: grey; margin-right: 5px;" @click=${() => this.removeValue(value)}>${this.getOptionLabel(value)} X</div>`)}`;
		} else {
			if(this.inputValue)
				return nothing;
			return html`
                <div class="placeholderOrValue">${this.getOptionLabel(this.selectedValue[0])}</div>`;
		}
	}

	renderInput() {
		return html`
            <aui-autosize-input
                    autocomplete="off"
                    autocorrect="off"
                    spellcheck="false"
                    ?disabled=${!this.isSearchable && !this.isCreatable}
                    .value=${ifDefined(this.inputValue)}
                    @input=${e => this._handleInputChange(e.target.value)}
            ></aui-autosize-input>
		`;
	}

	renderLoadingIndicator() {
		if(!this.isLoading)
			return nothing;
		return html`
            <div>L</div>`;
	}

	renderClearIndicator() {
		if(!this.isClearable || this.isDisabled || !this.hasValue() || this.isLoading)
			return nothing;

		return html`
            <md-icon class="clear" style="font-size: 19px;" @mousedown=${this._handleClearIndicatorMouseDown}>clear</md-icon>`;
	}

	renderDropdownIndicator() {
		return html`
            <md-icon class="dropdown" style="font-size: 19px;" @mousedown=${this._handleDropdownIndicatorMouseDown}>arrow_drop_down</md-icon>`;
	}

	renderMenu() {
		if(!this.isMenuOpen)
			return nothing;

		let menuUI;

		if(this.hasOptions()) {
			menuUI = this.menuOptions.render.map(o => {
				if(o.type === "group") {
					return html`
                        <div class="group">
                            <div class="label">${o.label}</div>
                            <div class="options">${o.options.map(go => this.renderOption(go))}</div>
                        </div>
					`;
				} else
					return this.renderOption(o);
			});
		} else if(this.isLoading) {
			menuUI = html`
                <div>Loading...</div>`;
		} else {
			menuUI = html`
                <div>No Options</div>`;
		}

		return html`
            <section class="menu" @mousedown=${this._handleMenuMouseDown}>${menuUI}</section>
		`;
	}

	renderOption(option) {
		const isFocused = option === this.focusedOption;

		return html`
            <aui-option
                    .label=${option.label}
                    .value=${option.value}

                    .isDisabled=${option.isDisabled}
                    .isFocused=${isFocused}
                    .isSelected=${option.isSelected}

                    @mouseover=${() => this._handleOptionHover(option)}
                    @click=${() => this.selectOption(option)}
            ></aui-option>
		`;
	}

}

customElements.define("aui-select", AuiSelect);